import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Search, Download, ChevronLeft, ChevronRight } from 'lucide-react'
import { accountingApi } from '@/modules/accounting/api/accounting.api'

const fmt = (n: number, showSign = false) => {
  if (n === 0) return '—'
  const s = new Intl.NumberFormat('fr-SN', { minimumFractionDigits: 0 }).format(Math.abs(Math.round(n)))
  return showSign ? (n >= 0 ? `+${s}` : `-${s}`) : s
}

export default function LedgerPage() {
  const [params] = useSearchParams()
  const [accountCode, setCode] = useState(params.get('account') ?? '')
  const [fyId, setFY]          = useState(params.get('fiscalYearId') ?? '')
  const [page, setPage]        = useState(1)

  const { data: fys = [] } = useQuery({ queryKey: ['fiscal-years'], queryFn: accountingApi.getFiscalYears })
  const activeFY = fyId || fys[0]?.id

  const { data: ledger, isLoading } = useQuery({
    queryKey: ['ledger', accountCode, activeFY, page],
    queryFn:  () => accountingApi.getLedger(accountCode, activeFY!, { page, limit: 50 }),
    enabled:  !!accountCode && !!activeFY,
  })

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts', accountCode],
    queryFn:  () => accountingApi.getAccounts({ search: accountCode, isActive: true }),
    enabled:  accountCode.length >= 2,
  })

  // Export CSV
  const exportCsv = () => {
    if (!ledger) return
    const header = 'Date;Journal;Référence;Libellé;Débit;Crédit;Solde\n'
    const rows = ledger.lines.map(l =>
      `${l.date};${l.journalCode};${l.reference};${l.description};${l.debitAmount || ''};${l.creditAmount || ''};${l.runningBalance}`
    ).join('\n')
    const blob = new Blob(['\uFEFF' + header + rows], { type: 'text/csv;charset=utf-8' })
    const url  = URL.createObjectURL(blob)
    const a    = Object.assign(document.createElement('a'), {
      href: url, download: `grand-livre-${accountCode}-${activeFY}.csv`,
    })
    a.click(); URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-5 nx-fade-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--tx)]">Grand Livre</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Mouvements détaillés par compte · Solde progressif</p>
        </div>
        {ledger && (
          <button
            onClick={exportCsv}
            className="flex items-center gap-2 px-3 py-2 border border-[var(--bd)] text-[var(--tx3)] hover:text-[var(--tx)] text-sm rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
        )}
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Sélecteur compte */}
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--tx3)]" />
          <input
            value={accountCode}
            onChange={e => { setCode(e.target.value); setPage(1) }}
            placeholder="Code ou libellé de compte…"
            className="w-full pl-9 pr-4 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] placeholder:text-[var(--tx3)] focus:outline-none focus:border-blue-500"
          />
          {/* Suggestions */}
          {accounts.length > 0 && accountCode.length >= 2 && !ledger && (
            <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-[var(--bd)] rounded-lg shadow-xl max-h-40 overflow-y-auto">
              {accounts.slice(0, 8).map(a => (
                <button
                  key={a.id}
                  onClick={() => setCode(a.code)}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-[#F0EDF8] flex items-center gap-2"
                >
                  <code className="text-[var(--blue-d)] font-mono w-14">{a.code}</code>
                  <span className="text-[var(--tx2)] truncate">{a.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <select
          value={fyId}
          onChange={e => { setFY(e.target.value); setPage(1) }}
          className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500"
        >
          {fys.map(fy => <option key={fy.id} value={fy.id}>{fy.label}</option>)}
        </select>
      </div>

      {/* Compte info */}
      {ledger && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Compte',        value: `${ledger.account.code} — ${ledger.account.name}`, mono: true },
            { label: 'Sens normal',   value: ledger.account.normalBalance },
            { label: 'Total débits',  value: `${fmt(ledger.totalDebit)} XOF` },
            { label: 'Total crédits', value: `${fmt(ledger.totalCredit)} XOF` },
          ].map(item => (
            <div key={item.label} className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl px-4 py-3">
              <p className="text-xs text-[var(--tx3)] mb-1">{item.label}</p>
              <p className={`text-sm font-semibold text-[var(--tx)] truncate ${item.mono ? 'font-mono' : ''}`}>{item.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      {!accountCode ? (
        <div className="text-center py-10 text-[var(--tx3)]">
          <Search className="w-8 h-8 mx-auto mb-3 opacity-40" />
          <p>Saisissez un code ou libellé de compte</p>
        </div>
      ) : isLoading ? (
        <div className="space-y-2">
          {[...Array(8)].map((_, i) => <div key={i} className="h-10 bg-[var(--surface-2)] rounded animate-pulse" />)}
        </div>
      ) : !ledger || ledger.lines.length === 0 ? (
        <div className="text-center py-10 text-[var(--tx3)]">Aucun mouvement pour ce compte sur cet exercice</div>
      ) : (
        <>
          <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--bd)] text-[var(--tx3)]">
                  <th className="text-left px-3 py-2.5 w-24">Date</th>
                  <th className="text-left px-3 py-2.5 w-16">Journal</th>
                  <th className="text-left px-3 py-2.5 w-32">Référence</th>
                  <th className="text-left px-3 py-2.5">Libellé</th>
                  <th className="text-right px-3 py-2.5 w-28">Débit (XOF)</th>
                  <th className="text-right px-3 py-2.5 w-28">Crédit (XOF)</th>
                  <th className="text-right px-3 py-2.5 w-32">Solde courant</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--bd)]">
                {ledger.lines.map((line, i) => (
                  <tr key={i} className="hover:bg-[var(--surface-2)]/20 group">
                    <td className="px-3 py-2 text-[var(--tx3)]">{line.date}</td>
                    <td className="px-3 py-2">
                      <span className="px-1.5 py-0.5 bg-[var(--surface-2)] rounded text-[var(--tx2)] font-mono">{line.journalCode}</span>
                    </td>
                    <td className="px-3 py-2 text-[var(--blue-d)] font-mono">{line.reference}</td>
                    <td className="px-3 py-2 text-[var(--tx2)] max-w-[240px] truncate">{line.description}</td>
                    <td className="px-3 py-2 text-right text-blue-300 font-medium tabular-nums">
                      {line.debitAmount > 0 ? fmt(line.debitAmount) : ''}
                    </td>
                    <td className="px-3 py-2 text-right text-emerald-300 font-medium tabular-nums">
                      {line.creditAmount > 0 ? fmt(line.creditAmount) : ''}
                    </td>
                    <td className={`px-3 py-2 text-right font-bold tabular-nums ${line.runningBalance >= 0 ? 'text-[var(--tx)]' : 'text-[var(--red-text)]'}`}>
                      {fmt(line.runningBalance, true)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Solde final */}
              <tfoot>
                <tr className="border-t-2 border-[var(--bd)] bg-[var(--surface-2)]/20">
                  <td colSpan={6} className="px-3 py-2.5 text-xs font-bold text-[var(--tx3)] text-right uppercase">
                    Solde clôture
                  </td>
                  <td className={`px-3 py-2.5 text-right text-sm font-bold tabular-nums ${
                    ledger.closingBalance >= 0 ? 'text-[var(--tx)]' : 'text-[var(--red-text)]'
                  }`}>
                    {fmt(ledger.closingBalance, true)} XOF
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Pagination */}
          {ledger.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-[var(--tx3)]">
                {ledger.total} mouvements · Page {ledger.page}/{ledger.pages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 border border-[var(--bd)] rounded-lg text-[var(--tx3)] hover:text-[var(--tx)] disabled:opacity-40 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-[var(--tx3)]">{page}</span>
                <button
                  onClick={() => setPage(p => Math.min(ledger.pages, p + 1))}
                  disabled={page >= ledger.pages}
                  className="p-2 border border-[var(--bd)] rounded-lg text-[var(--tx3)] hover:text-[var(--tx)] disabled:opacity-40 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
